import {
	CheckCircle,
	CreditCard,
	Download,
	ExternalLink,
	XCircle,
} from "lucide-react";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import { useTenant } from "@/contexts/TenantContext";
import { formatCurrency, formatDate } from "@/lib/utils";

// Mock data - in real app, this would come from API
const invoices = [
	{
		id: "inv_001",
		number: "INV-2024-001",
		status: "paid",
		total: 4900,
		dueDate: "2024-01-15",
		paidAt: "2024-01-15",
		periodStart: "2024-01-01",
		periodEnd: "2024-01-31",
		hostedInvoiceUrl: "#",
		invoicePdf: "#",
	},
	{
		id: "inv_002",
		number: "INV-2023-012",
		status: "paid",
		total: 4900,
		dueDate: "2023-12-15",
		paidAt: "2023-12-15",
		periodStart: "2023-12-01",
		periodEnd: "2023-12-31",
		hostedInvoiceUrl: "#",
		invoicePdf: "#",
	},
];

const paymentMethods = [
	{
		id: "pm_001",
		type: "card",
		brand: "visa",
		last4: "4242",
		expMonth: 12,
		expYear: 2025,
		isDefault: true,
	},
];

export function BillingPage() {
	const { subscription, plan, usage, isLoading } = useTenant();
	const [isCreatingPortal, setIsCreatingPortal] = useState(false);

	const handleManageBilling = async () => {
		setIsCreatingPortal(true);
		try {
			// In real app, call API to create portal session
			window.open("https://billing.stripe.com/p/session/test_portal", "_blank");
		} catch (error) {
			console.error("Failed to open billing portal:", error);
		} finally {
			setIsCreatingPortal(false);
		}
	};

	if (isLoading) {
		return <div>Loading...</div>;
	}

	return (
		<div className="space-y-6">
			{/* Page header */}
			<div>
				<h1 className="text-2xl font-bold text-gray-900">Billing & Usage</h1>
				<p className="text-gray-500 mt-1">
					Manage your subscription, usage, and billing information
				</p>
			</div>

			{/* Current plan */}
			<Card>
				<CardHeader>
					<CardTitle className="flex items-center space-x-2">
						<CreditCard className="w-5 h-5" />
						<span>Current Plan</span>
					</CardTitle>
					<CardDescription>
						Your active subscription plan and billing cycle
					</CardDescription>
				</CardHeader>
				<CardContent className="space-y-4">
					<div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
						<div>
							<h3 className="font-semibold text-lg">
								{plan?.displayName || "Free Plan"}
							</h3>
							<p className="text-gray-500 text-sm">{plan?.description}</p>
							{subscription && (
								<div className="mt-2 space-y-1">
									<p className="text-sm text-gray-600">
										<span className="font-medium">Status:</span>{" "}
										<span
											className={`capitalize ${
												subscription.status === "active"
													? "text-green-600"
													: subscription.status === "trialing"
														? "text-blue-600"
														: "text-red-600"
											}`}
										>
											{subscription.status}
										</span>
									</p>
									<p className="text-sm text-gray-600">
										<span className="font-medium">Billing cycle:</span>{" "}
										<span className="capitalize">
											{subscription.billingCycle}
										</span>
									</p>
									<p className="text-sm text-gray-600">
										<span className="font-medium">Next billing:</span>{" "}
										{formatDate(subscription.currentPeriodEnd)}
									</p>
								</div>
							)}
						</div>
						<div className="text-right">
							{plan && plan.priceMonthly > 0 ? (
								<div>
									<div className="text-2xl font-bold">
										{formatCurrency(plan.priceMonthly)}
									</div>
									<div className="text-sm text-gray-500">
										/
										{subscription?.billingCycle === "yearly" ? "year" : "month"}
									</div>
								</div>
							) : (
								<div className="text-2xl font-bold text-green-600">Free</div>
							)}
						</div>
					</div>

					<div className="flex space-x-3">
						{plan?.name === "free" ? (
							<Button>Upgrade Plan</Button>
						) : (
							<Button
								variant="outline"
								onClick={handleManageBilling}
								disabled={isCreatingPortal}
							>
								{isCreatingPortal ? (
									<div className="flex items-center space-x-2">
										<div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600" />
										<span>Opening...</span>
									</div>
								) : (
									<>
										<ExternalLink className="w-4 h-4 mr-2" />
										Manage Billing
									</>
								)}
							</Button>
						)}
					</div>
				</CardContent>
			</Card>

			{/* Usage overview */}
			{usage && (
				<Card>
					<CardHeader>
						<CardTitle>Usage Overview</CardTitle>
						<CardDescription>
							Current usage against your plan limits
						</CardDescription>
					</CardHeader>
					<CardContent>
						<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
							{Object.entries(usage).map(([key, metric]) => (
								<div key={key} className="space-y-2">
									<div className="flex items-center justify-between text-sm">
										<span className="font-medium capitalize">
											{key.replace("_", " ")}
										</span>
										<span className="text-gray-500">
											{metric.current} /{" "}
											{metric.limit === -1 ? "∞" : metric.limit}
										</span>
									</div>
									<div className="w-full bg-gray-200 rounded-full h-2">
										<div
											className={`h-2 rounded-full transition-all ${
												metric.percentage > 90
													? "bg-red-500"
													: metric.percentage > 75
														? "bg-orange-500"
														: "bg-green-500"
											}`}
											style={{ width: `${Math.min(metric.percentage, 100)}%` }}
										/>
									</div>
									<div className="text-xs text-gray-500">
										{metric.percentage.toFixed(1)}% used
									</div>
								</div>
							))}
						</div>
					</CardContent>
				</Card>
			)}

			{/* Payment methods */}
			<Card>
				<CardHeader>
					<CardTitle>Payment Methods</CardTitle>
					<CardDescription>
						Manage your payment methods and billing address
					</CardDescription>
				</CardHeader>
				<CardContent>
					{paymentMethods.length > 0 ? (
						<div className="space-y-3">
							{paymentMethods.map((method) => (
								<div
									key={method.id}
									className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
								>
									<div className="flex items-center space-x-3">
										<div className="w-8 h-5 bg-gray-100 rounded flex items-center justify-center">
											<span className="text-xs font-semibold text-gray-600 uppercase">
												{method.brand}
											</span>
										</div>
										<div>
											<p className="text-sm font-medium">
												•••• •••• •••• {method.last4}
											</p>
											<p className="text-xs text-gray-500">
												Expires {method.expMonth}/{method.expYear}
												{method.isDefault && " • Default"}
											</p>
										</div>
									</div>
									<Button variant="ghost" size="sm">
										Edit
									</Button>
								</div>
							))}
							<Button variant="outline" size="sm">
								Add Payment Method
							</Button>
						</div>
					) : (
						<div className="text-center py-6 text-gray-500">
							<CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
							<p className="text-sm">No payment methods on file</p>
							<Button variant="outline" size="sm" className="mt-2">
								Add Payment Method
							</Button>
						</div>
					)}
				</CardContent>
			</Card>

			{/* Billing history */}
			<Card>
				<CardHeader>
					<CardTitle>Billing History</CardTitle>
					<CardDescription>
						Download invoices and view billing history
					</CardDescription>
				</CardHeader>
				<CardContent>
					{invoices.length > 0 ? (
						<div className="space-y-3">
							{invoices.map((invoice) => (
								<div
									key={invoice.id}
									className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
								>
									<div className="flex items-center space-x-3">
										<div className="flex items-center space-x-2">
											{invoice.status === "paid" ? (
												<CheckCircle className="w-4 h-4 text-green-500" />
											) : (
												<XCircle className="w-4 h-4 text-red-500" />
											)}
											<span className="text-sm font-medium">
												{invoice.number}
											</span>
										</div>
									</div>

									<div className="text-sm text-gray-600">
										{formatDate(invoice.periodStart)} -{" "}
										{formatDate(invoice.periodEnd)}
									</div>

									<div className="flex items-center space-x-4">
										<span className="text-sm font-medium">
											{formatCurrency(invoice.total)}
										</span>
										<div className="flex space-x-2">
											<Button variant="ghost" size="sm" asChild>
												<a
													href={invoice.hostedInvoiceUrl}
													target="_blank"
													rel="noopener noreferrer"
												>
													<ExternalLink className="w-3 h-3 mr-1" />
													View
												</a>
											</Button>
											<Button variant="ghost" size="sm" asChild>
												<a href={invoice.invoicePdf} download>
													<Download className="w-3 h-3 mr-1" />
													PDF
												</a>
											</Button>
										</div>
									</div>
								</div>
							))}
						</div>
					) : (
						<div className="text-center py-6 text-gray-500">
							<CreditCard className="w-8 h-8 mx-auto mb-2 text-gray-300" />
							<p className="text-sm">No billing history</p>
						</div>
					)}
				</CardContent>
			</Card>
		</div>
	);
}
